import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography, Grid } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import {
  FBoxContainer,
  FProgressBar,
  FHButton,
  FHInputField,
  FHAutocomplete,
  FHPageLink,
} from '../../../components/base';
import { behaviorActions } from '../../../store';
import { trimsValuesInObject } from '../../../utils/common';
import PageContainer from '../../../components/layout/PageContainer';
import { STANDARD_ERROR_MESSAGES } from '../../../constants';

import type { RootState } from '../../../store';
import type { ChangeEvent } from 'react';
import type { IBehavior } from '../../../utils/types';

function AddGoalTemplateForm() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const { required } = STANDARD_ERROR_MESSAGES;

  const { list: behaviorList, loading: goalNamesLoading } = useSelector(
    (state: RootState) => state.behaviors,
  );

  const [selectedBehavior, setSelectedBehavior] = useState<IBehavior | null>(null);

  useEffect(() => {
    dispatch(behaviorActions.getAllGoalTemplates({ getAccessTokenSilently }));
    dispatch(behaviorActions.getList({ getAccessTokenSilently }));
  }, [dispatch, getAccessTokenSilently]);

  const goalTemplateValidationSchema = Yup.object().shape({
    label: Yup.string().trim().required(required),
    prefix: Yup.string().trim().required(required),
  });

  const createGoalTemplate = async (values: any) => {
    if (!selectedBehavior?.id) {
      toast.error('Please select a focus area');
      return null;
    }
    const trimedValues = trimsValuesInObject(values);
    const { behavior_id: _behavior_id, ...remaningFields } = trimedValues;
    try {
      const createGoalTemplateResponse = await dispatch(
        behaviorActions.createGoalName({
          behaviorId: selectedBehavior?.id,
          data: { ...remaningFields },
          getAccessTokenSilently,
        }),
      );
      if (createGoalTemplateResponse?.error) {
        toast.error(createGoalTemplateResponse.error);
      }

      return createGoalTemplateResponse?.payload?.id;
    } catch {
      toast.error('An error occurred while creating goal template');
      return null;
    }
  };

  const formik = useFormik({
    initialValues: {
      label: '',
      prefix: '',
      behavior_id: '',
    },

    onSubmit: async (values: any) => {
      const response = await createGoalTemplate(values);
      if (!response) return;
      // Reset Form after submission
      formik.resetForm();
      setSelectedBehavior(null);
    },
    validationSchema: goalTemplateValidationSchema,
  });
  const { values, setFieldValue, submitForm, errors, touched } = formik;

  const handleUpdateFieldValues = (key: string, value: any) => {
    const formattedValue = value;
    setFieldValue(key, formattedValue);
  };

  return (
    <PageContainer>
      {goalNamesLoading && <FProgressBar />}
      <Box sx={{ mt: 3 }}>
        <FHPageLink to="/admin" label="Admin Home" />
        <FBoxContainer sx={{ borderRadius: '20px', boxShadow: 'none' }}>
          <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
            <Grid container>
              <Grid item xs={12} md={12} lg={8}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box ml={1} sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <Typography textAlign={'left'} variant="h4">
                      Add Goal Template
                    </Typography>
                  </Box>
                  <Box
                    sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mt: 1, mb: 2 }}
                  />
                  <Grid container p={0.5} spacing={1}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>Name:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHInputField
                        inputHeight={40}
                        fullWidth
                        sx={{ boxShadow: 'none' }}
                        value={values['label']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('label', e.target.value)
                        }
                        error={touched.label && Boolean(errors.label)}
                        helperText={touched.label && errors.label?.toString()}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>Prefix:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHInputField
                        inputHeight={40}
                        fullWidth
                        sx={{ boxShadow: 'none' }}
                        value={values['prefix']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('prefix', e.target.value)
                        }
                        error={touched.prefix && Boolean(errors.prefix)}
                        helperText={touched.prefix && errors.prefix?.toString()}
                      />
                    </Grid>
                  </Grid>

                  <Grid container p={0.5} spacing={1}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>Focus Area:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHAutocomplete
                        inputHeight={40}
                        fullWidth
                        sx={{ boxShadow: 'none' }}
                        placeholder="Select Focus Area"
                        isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                        options={behaviorList.map((b: IBehavior) => ({
                          id: b?.id,
                          label: `${b?.label}`,
                        }))}
                        value={selectedBehavior}
                        onChange={(
                          event: ChangeEvent<HTMLSelectElement>,
                          newValue: IBehavior | null,
                        ) => {
                          setSelectedBehavior(newValue);
                          handleUpdateFieldValues('behavior_id', newValue?.id);
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box ml={1} mt={3} sx={{ display: 'flex' }}>
                  <FHButton disabled={goalNamesLoading} onClick={submitForm}>
                    Submit
                  </FHButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FBoxContainer>
      </Box>
    </PageContainer>
  );
}

export default AddGoalTemplateForm;
